import { useMemo, useState } from 'react';
import axios from 'axios';
import { ChevronLeft, ChevronRight, ChevronDown, Users, Search, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { useAuth } from '../../context/AuthContext';
import API_BASE from '../../utils/apiBase';
import { isApplicantStatus, normalizeLifecycleStatus } from '../../lib/lifecycle';
import {
  adjacentApplicantIds, cleanApplicantRows, createApplicantNavigation, readApplicantNavigation,
} from '../../lib/applicantNavigation';

/**
 * Applicant-only, compact same-workspace navigation. Candidate names originate
 * from the existing tenant-scoped Recruitment response, NEVER a global user list.
 * Opening another profile still passes through its own backend authorization.
 */
export default function ApplicantQuickSwitch({ employeeId, navigationContext, onSwitch }) {
  const { token } = useAuth();
  const safeContext = readApplicantNavigation(navigationContext);
  const [applicants, setApplicants] = useState(() => safeContext?.applicants || []);
  const [allLoaded, setAllLoaded] = useState(false);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const adjacent = useMemo(() => adjacentApplicantIds(applicants, employeeId), [applicants, employeeId]);
  const matches = useMemo(() => applicants.filter((item) =>
    item.name.toLowerCase().includes(query.trim().toLowerCase())
  ), [applicants, query]);

  const fetchAllApplicants = async () => {
    if (!token || loading) return;
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(API_BASE + '/recruitment/applicants', {
        headers: { Authorization: 'Bearer ' + token },
      });
      const scopedApplicants = (Array.isArray(response.data) ? response.data : []).filter(
        (person) => isApplicantStatus(normalizeLifecycleStatus(person?.status))
      );
      setApplicants(cleanApplicantRows(scopedApplicants));
      setAllLoaded(true);
    } catch {
      setError('Applicant list unavailable. The current profile has not changed.');
    } finally {
      setLoading(false);
    }
  };

  const openSwitcher = (nextOpen) => {
    setOpen(nextOpen);
    if (nextOpen && (applicants.length === 0 || safeContext?.incomplete) && !allLoaded) {
      void fetchAllApplicants();
    }
  };

  const selectApplicant = (id) => {
    if (!id || id === employeeId || !applicants.some((item) => item.id === id)) return;
    setOpen(false);
    onSwitch(id, createApplicantNavigation(applicants, id, safeContext?.filters || {}));
  };

  return (
    <div className="shrink-0" data-testid="applicant-quick-switch">
      <Popover open={open} onOpenChange={openSwitcher}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-9 gap-2 rounded-xl border-slate-200 bg-white px-3 text-slate-800 shadow-sm hover:bg-slate-50"
            data-testid="applicant-switch-open"
            aria-label="Switch applicant"
          >
            <Users className="h-4 w-4 text-slate-600" aria-hidden="true" />
            <span>Switch applicant</span>
            <ChevronDown className="h-3.5 w-3.5 text-slate-500" aria-hidden="true" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" sideOffset={8} className="z-[120] w-[min(92vw,350px)] rounded-xl border border-slate-200 bg-white p-2 shadow-lg">
          <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-2 pb-2 pt-1">
            <div>
              <p className="text-sm font-semibold text-slate-900">Switch applicant</p>
              <p className="text-xs text-slate-500">Your current recruitment results</p>
            </div>
            <div className="flex items-center gap-1">
              <Button
                type="button" variant="ghost" size="sm"
                className="h-8 w-8 p-0"
                aria-label="Previous applicant" title="Previous applicant"
                disabled={!adjacent.previous}
                onClick={() => selectApplicant(adjacent.previous)}
                data-testid="applicant-previous"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="min-w-10 text-center text-xs text-slate-500" data-testid="applicant-switch-position">
                {adjacent.position !== null ? `${adjacent.position}/${adjacent.total}` : '—'}
              </span>
              <Button
                type="button" variant="ghost" size="sm"
                className="h-8 w-8 p-0"
                aria-label="Next applicant" title="Next applicant"
                disabled={!adjacent.next}
                onClick={() => selectApplicant(adjacent.next)}
                data-testid="applicant-next"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <label className="flex items-center gap-2 px-1 py-2" htmlFor="applicant-switch-search">
            <Search className="h-4 w-4 shrink-0 text-slate-500" aria-hidden="true" />
            <Input id="applicant-switch-search" autoFocus value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search applicants by name" aria-label="Find applicant by name"
              className="h-9 rounded-lg" />
          </label>
          <div className="max-h-60 overflow-y-auto" role="group" aria-label="Applicants">
            {matches.slice(0, 50).map((person) => (
              <button key={person.id} type="button"
                className="flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2.5 text-left text-sm text-slate-800 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500 disabled:bg-slate-50 disabled:font-semibold"
                disabled={person.id === employeeId}
                aria-current={person.id === employeeId ? 'page' : undefined}
                onClick={() => selectApplicant(person.id)}>
                <span className="truncate">{person.name}</span>
                {person.id === employeeId && <span className="shrink-0 text-xs font-normal text-slate-500">Current</span>}
              </button>
            ))}
            {!loading && matches.length === 0 && (
              <p className="px-3 py-3 text-sm text-slate-600">No matching applicants.</p>
            )}
            {matches.length > 50 && (
              <p className="px-3 py-2 text-xs text-slate-600">Type a name to narrow the list.</p>
            )}
          </div>
          {loading && <p className="flex items-center gap-2 px-2 py-2 text-xs text-slate-600"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading applicants…</p>}
          {error && <p role="alert" className="px-2 py-2 text-xs text-amber-700">{error}</p>}
          {!allLoaded && applicants.length > 0 && (
            <button type="button" onClick={fetchAllApplicants} disabled={loading}
              className="mt-1 w-full rounded-md border-t border-slate-100 px-2 py-2 text-left text-xs text-blue-700 hover:bg-slate-50 disabled:opacity-50"
              data-testid="applicant-load-all">Find from all applicants</button>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
